import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';

import { useAllServicesCost } from '../hooks/useCostData';
import type { ServiceCost } from '../types';
import { RangeControls } from '../components/RangeControls';
import { VendorBreakdown } from '../components/VendorBreakdown';
import { mergeVendorCosts } from '../vendors';
import {
  EMPTY_CUSTOM_RANGE,
  INTERVAL_MS,
  bucketLabelFormat,
  formatUsd,
  intervalForSpan,
  resolveRange,
  type CustomRange,
  type IntervalKey,
  type RangeKey,
} from '../time';

// Stable color per service so the chart + table agree.
const SERVICE_COLORS: Record<ServiceCost['key'], string> = {
  merch: '#6366f1',
  video: '#0ea5e9',
  interior: '#10b981',
  ads: '#f59e0b',
  mobile: '#ec4899',
  genapi: '#a855f7',
};

interface BucketRow {
  label: string;
  [serviceKey: string]: string | number;
}

/** Window used while a custom range is still incomplete — nothing is fetched. */
const NO_RANGE = { from: new Date(0), to: new Date(0) };

export function AllServicesCostPage() {
  const [rangeKey, setRangeKey] = useState<RangeKey>('7d');
  const [custom, setCustom] = useState<CustomRange>(EMPTY_CUSTOM_RANGE);
  const [intervalKey, setIntervalKey] = useState<IntervalKey>(intervalForSpan(7 * 24 * 60 * 60_000));
  const [reloadKey, setReloadKey] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const resolved = useMemo(
    () => resolveRange(rangeKey, custom),
    // reloadKey re-snapshots "now" so Refresh re-fetches the latest data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangeKey, custom, reloadKey],
  );
  const range = resolved ?? NO_RANGE;

  const { services, loading, error } = useAllServicesCost(range);

  function selectRange(key: RangeKey) {
    setRangeKey(key);
    const next = resolveRange(key, custom);
    if (next) setIntervalKey(intervalForSpan(next.to.getTime() - next.from.getTime()));
  }

  function selectCustom(next: CustomRange) {
    setCustom(next);
    const resolvedCustom = resolveRange('custom', next);
    if (resolvedCustom) {
      setIntervalKey(intervalForSpan(resolvedCustom.to.getTime() - resolvedCustom.from.getTime()));
    }
  }

  function toggleService(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const grandTotal = services.reduce((acc, s) => acc + s.totalCost, 0);
  const totalScans = services
    .filter((s) => s.unit === 'scans')
    .reduce((acc, s) => acc + s.count, 0);
  const avgPerScan = totalScans ? grandTotal / totalScans : 0;

  // Cross-service vendor split, shown as the total row of "By service".
  const allVendors = useMemo(
    () => mergeVendorCosts(services.map((s) => s.vendors)),
    [services],
  );

  // Client-side time bucketing → one stacked bar per service.
  const buckets = useMemo<BucketRow[]>(() => {
    const step = INTERVAL_MS[intervalKey];
    const end = range.to.getTime();
    const start = Math.floor(range.from.getTime() / step) * step;
    const labelFmt = bucketLabelFormat(step, end - range.from.getTime());

    const map = new Map<number, Record<string, number>>();
    for (let t = start; t <= end; t += step) {
      const zero: Record<string, number> = {};
      for (const s of services) zero[s.key] = 0;
      map.set(t, zero);
    }
    for (const s of services) {
      for (const item of s.items) {
        const bucket = Math.floor(item.t / step) * step;
        const entry = map.get(bucket);
        if (!entry) continue;
        entry[s.key] = (entry[s.key] || 0) + item.cost;
      }
    }
    return Array.from(map.entries()).map(([t, v]) => ({
      label: format(new Date(t), labelFmt),
      ...v,
    }));
  }, [services, range, intervalKey]);

  const hasCost = grandTotal > 0;

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">All Services Cost</h1>
        <p className="text-sm text-slate-500 mt-1">
          Cost per scan across every service in the selected time frame, broken down by vendor.
        </p>
      </div>
      <RangeControls
        rangeKey={rangeKey}
        onRangeKeyChange={selectRange}
        custom={custom}
        onCustomChange={selectCustom}
        intervalKey={intervalKey}
        onIntervalKeyChange={setIntervalKey}
        range={resolved}
        onRefresh={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );

  // A half-filled custom range covers no window. The totals still in state
  // belong to the previously selected range, so showing them here would put a
  // number next to a range it was not measured over. Prompt instead.
  if (!resolved) {
    return (
      <div className="max-w-7xl mx-auto space-y-4">
        {header}
        <div className="rounded-lg border border-slate-200 bg-slate-50 text-slate-600 px-4 py-3 text-sm">
          Pick a start and end date to load costs for a custom period.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {header}

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm">
          Failed to load cost data: {error}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Total cost" value={formatUsd(grandTotal)} sub="all services, this period" />
        <StatCard label="Total scans" value={String(totalScans)} sub="completed scans (excl. gen-api calls)" />
        <StatCard label="Avg cost / scan" value={formatUsd(avgPerScan)} sub="across all scan services" />
      </div>

      {/* Cost over time (stacked by service) */}
      <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Cost over time</h3>
          <div className="text-right">
            <span className="text-lg font-bold text-slate-700">{formatUsd(grandTotal)}</span>
            <span className="ml-1 text-[10px] text-slate-500 uppercase">total</span>
          </div>
        </div>
        <div className="h-64">
          {loading && services.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              Loading cost data…
            </div>
          ) : hasCost ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={buckets} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v: number) => formatUsd(v)} />
                <Tooltip
                  formatter={(value: number, name: string) => [formatUsd(value), name]}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontSize: '12px',
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={24}
                  formatter={(value: string) => <span className="text-slate-600 text-xs">{value}</span>}
                />
                {services.map((s) => (
                  <Bar key={s.key} dataKey={s.key} name={s.label} stackId="a" fill={SERVICE_COLORS[s.key]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-slate-400">
              No cost in this period
            </div>
          )}
        </div>
      </div>

      {/* Per-service breakdown, expandable into per-vendor rows */}
      <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">By service</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Expand a service to see which vendors its spend went to.
            </p>
          </div>
          <div className="text-right">
            <span className="text-lg font-bold text-slate-700">{services.length}</span>
            <span className="ml-1 text-[10px] text-slate-500 uppercase">services</span>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          <div className="hidden sm:grid grid-cols-[1fr_repeat(3,minmax(0,8rem))] gap-2 pb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            <span>Service</span>
            <span className="text-right">Scans / calls</span>
            <span className="text-right">Total cost</span>
            <span className="text-right">Avg / unit</span>
          </div>
          {services.map((s) => {
            const avg = s.count ? s.totalCost / s.count : 0;
            const isOpen = expanded.has(s.key);
            return (
              <div key={s.key}>
                <button
                  type="button"
                  onClick={() => toggleService(s.key)}
                  aria-expanded={isOpen}
                  className="w-full text-left grid grid-cols-2 sm:grid-cols-[1fr_repeat(3,minmax(0,8rem))] gap-2 py-2.5 text-sm items-center hover:bg-slate-50/70 rounded-lg"
                >
                  <span className="font-medium text-slate-800 col-span-2 sm:col-span-1 flex items-center gap-2 min-w-0">
                    <Chevron open={isOpen} />
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                      style={{ backgroundColor: SERVICE_COLORS[s.key] }}
                    />
                    <span className="whitespace-nowrap">{s.label}</span>
                    {s.vendors.length > 0 && (
                      <span className="hidden md:inline text-[10px] text-slate-400 shrink-0">
                        {`${s.vendors.length} vendor${s.vendors.length > 1 ? 's' : ''}`}
                      </span>
                    )}
                    {s.warning && (
                      <span className="text-[10px] text-amber-600 shrink-0" title={s.warning}>
                        (unavailable)
                      </span>
                    )}
                  </span>
                  <span className="text-right tabular-nums text-slate-700" data-label="Scans / calls">
                    <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">
                      {s.unit === 'calls' ? 'Calls' : 'Scans'}
                    </span>
                    {s.count} {s.unit === 'calls' ? 'calls' : ''}
                  </span>
                  <span className="text-right tabular-nums text-slate-700" data-label="Total cost">
                    <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Cost</span>
                    {formatUsd(s.totalCost)}
                  </span>
                  <span className="text-right tabular-nums text-slate-700" data-label="Avg / unit">
                    <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Avg</span>
                    {formatUsd(avg)}
                  </span>
                </button>
                {isOpen && (
                  <VendorBreakdown
                    vendors={s.vendors}
                    total={s.totalCost}
                    nested
                    emptyLabel={
                      s.key === 'genapi'
                        ? 'Per-vendor gen-api spend is unavailable — the gen-api service has not reported a provider split for this window.'
                        : 'No vendor spend recorded for this service in this period.'
                    }
                  />
                )}
              </div>
            );
          })}

          {/* Cross-service vendor total */}
          <div>
            <button
              type="button"
              onClick={() => toggleService('__all__')}
              aria-expanded={expanded.has('__all__')}
              className="w-full text-left grid grid-cols-2 sm:grid-cols-[1fr_repeat(3,minmax(0,8rem))] gap-2 py-2.5 text-sm items-center hover:bg-slate-50/70 rounded-lg"
            >
              <span className="font-semibold text-slate-800 col-span-2 sm:col-span-1 flex items-center gap-2 whitespace-nowrap">
                <Chevron open={expanded.has('__all__')} />
                Total by vendor
              </span>
              <span />
              <span className="text-right tabular-nums font-semibold text-slate-800">
                {formatUsd(grandTotal)}
              </span>
              <span />
            </button>
            {expanded.has('__all__') && (
              <VendorBreakdown vendors={allVendors} total={grandTotal} nested />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-3 h-3 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}
